<template>
  <div id="credit-returns-history" class="align-start fill-height">
    <v-row class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0" :ref="pageRef">{{ $route.meta.title }}</h2>

        <v-row>
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': selectedView === APPROVED }"
              @click="handleViewChange(APPROVED)"
            >
              Approved
            </v-btn>
          </v-col>
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': selectedView === REJECTED }"
              @click="handleViewChange(REJECTED)"
            >
              Rejected
            </v-btn>
          </v-col>

          <v-spacer></v-spacer>

          <v-col cols="12" sm="4" md="3" lg="2">
            <i-instructions
              instructionKey="reports/credit-returns-history"
              :instruction-section="instructionSection"
            ></i-instructions>
          </v-col>
        </v-row>

        <i-search-sort
          ref="searchSort"
          :loading="loading"
          dateRangeColumnName="Created Date"
          :routeName="$route.name"
          @searchQuery="searchQuery"
        />

        <i-progress-linear v-if="loading && $vuetifyDisplay.smAndDown" />

        <template v-if="!loading && creditReturns.length === 0">
          <i-empty-results
            buttonText="Back to Reports"
            buttonRouteName="reports"
            :title="emptyFilteredResultsText"
          />
        </template>

        <template v-else>
          <template v-if="$vuetifyDisplay.smAndDown">
            <v-row no-gutters>
              <v-alert
                style="width: 100%"
                class="mt-2"
                border="left"
                dense
                text
                outlined
                type="info"
              >
                <strong
                  >Variable Weight Products are excluded from Credit
                  Returns</strong
                >
              </v-alert>

              <v-col
                cols="12"
                v-for="item in creditReturns"
                :key="item.id"
                class="mt-0 pa-0"
              >
                <v-card class="rounded-2 pa-4 mb-6">
                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span
                        ><strong>{{ headers[0].text }}</strong></span
                      >
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span
                        ><strong>{{ item.referenceNumber }}</strong></span
                      >
                    </v-col>
                  </v-row>

                  <v-divider class="my-2"></v-divider>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[1].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.formatDateTime12h(item.createdAt)
                      }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[2].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.formatDateTime12h(item.resolvedAt)
                      }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[3].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{ item.store.name }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[4].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.currency(
                          displayCreditReturnTotal(item.supplierTotalIncFee)
                        )
                      }}</span>
                    </v-col>
                  </v-row>

                  <v-divider class="my-2"></v-divider>

                  <div>
                    <p
                      v-if="item.status === OUTSTANDING"
                      class="g-bold g-border-radius-s i-status-block-orange px-2 py-3 text-center"
                    >
                      CREDIT RETURN STATUS: OUTSTANDING
                    </p>
                    <p
                      v-if="item.status === REJECTED"
                      class="g-bold g-border-radius-s i-status-block-red px-2 py-3 text-center"
                    >
                      CREDIT RETURN STATUS: REJECTED
                    </p>
                    <p
                      v-if="item.status === APPROVED"
                      class="g-bold g-border-radius-s i-status-block-green px-2 py-3 text-center"
                    >
                      CREDIT RETURN STATUS:
                      {{ item.hasRejectedItems ? 'PARTIALLY' : '' }} APPROVED
                    </p>
                  </div>

                  <v-divider class="my-2"></v-divider>

                  <v-btn
                    rounded
                    class="i-btn-block-secondary g-full-width"
                    :to="{
                      name: 'credit-return-details',
                      params: { id: item.id }
                    }"
                  >
                    View
                  </v-btn>

                  <div
                    class="text-center pt-2"
                    v-if="
                      item.status === APPROVED &&
                      item.paymentTransactionExpectedPeriodEndDate
                    "
                  >
                    <v-btn
                      class="g-full-width i-btn-block-primary mt-1"
                      :to="paymentTransactionsRouteTo(item)"
                    >
                      <span>Payment Transaction Details</span>
                      <v-icon color="white" class="ml-1"
                        >mdi-list-box-outline</v-icon
                      >
                    </v-btn>
                  </div>

                  <div class="text-center" v-if="item.status === APPROVED">
                    <v-btn
                      class="g-full-width i-btn-block-primary mt-2"
                      @click="fetchPDF(item.id)"
                    >
                      <span>Export Credit Return</span>
                      <v-icon color="white" class="ml-1"
                        >mdi-file-pdf-box</v-icon
                      >
                    </v-btn>
                    <v-btn
                      class="g-full-width i-btn-block-primary mt-2"
                      @click="fetchInvoicePDF(item.id)"
                      :disabled="!item.invoiceNumber"
                    >
                      <span>Export Fees Invoice</span>
                      <v-icon color="white" class="ml-1"
                        >mdi-file-pdf-box</v-icon
                      >
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <template v-else>
            <v-row no-gutters>
              <v-col cols="12">
                <v-data-table
                  class="mt-2 mt-md-4 pt-2 elevation-1 i-data-table"
                  :headers="headersForSelectedView"
                  :loading="loading"
                  :items="creditReturns"
                  :items-per-page="pageSize"
                  :no-data-text="emptyFilteredResultsText"
                  :no-results-text="emptyFilteredResultsText"
                  hide-default-footer
                >
                  <template #top>
                    <v-row class="mt-2 mb-n2" no-gutters>
                      <v-col cols="12" md="5" lg="4" class="px-3">
                        <v-alert border="left" dense text outlined type="info">
                          <strong
                            >Variable Weight Products are excluded from Credit
                            Returns</strong
                          >
                        </v-alert>
                      </v-col>
                    </v-row>
                  </template>

                  <template #[`header.actions`]="{ header }">
                    {{ header.text }}
                    <v-icon
                      class="i-tooltip-color pb-1"
                      small
                      v-tooltip="
                        'PDF only available for APPROVED Credit Returns'
                      "
                    >
                      mdi-help-circle-outline
                    </v-icon>
                  </template>

                  <template v-slot:[`header.referenceNumber`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('referenceNumber')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('referenceNumber')" small>
                        {{
                          isSortAsc('referenceNumber')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.createdAt`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('createdAt')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('createdAt')" small>
                        {{
                          isSortAsc('createdAt')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.resolvedAt`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('resolvedAt')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('resolvedAt')" small>
                        {{
                          isSortAsc('resolvedAt')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.store.name`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('store.name')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('store.name')" small>
                        {{
                          isSortAsc('store.name')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`item.referenceNumber`]="{ item }">
                    <span>{{ item.referenceNumber }}</span>
                  </template>

                  <template
                    v-slot:[`item.supplierTotalIncFee.amountIncGst`]="{ item }"
                  >
                    <span>{{
                      $filters.currency(
                        displayCreditReturnTotal(item.supplierTotalIncFee)
                      )
                    }}</span>
                  </template>

                  <template v-slot:[`item.createdAt`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.createdAt)
                    }}</span>
                  </template>

                  <template v-slot:[`item.resolvedAt`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.resolvedAt)
                    }}</span>
                  </template>

                  <template v-slot:[`item.store.name`]="{ item }">
                    <span>{{ item.store.name }}</span>
                  </template>

                  <template v-slot:[`item.details`]="{ item }">
                    <v-btn
                      rounded
                      class="i-btn-block-secondary p-btn"
                      :to="{
                        name: 'credit-return-details',
                        params: { id: item.id }
                      }"
                    >
                      <span><strong>View</strong></span>
                    </v-btn>
                  </template>

                  <template v-slot:[`item.actions`]="{ item }">
                    <v-btn
                      :disabled="!item.paymentTransactionExpectedPeriodEndDate"
                      icon
                      :to="paymentTransactionsRouteTo(item)"
                      v-tooltip="{
                        content: 'View related Payment Transaction details',
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                    >
                      <v-icon class="i-btn-payment-transactions"
                        >mdi-list-box-outline</v-icon
                      >
                    </v-btn>
                    <v-btn
                      :disabled="item.status !== APPROVED"
                      icon
                      v-tooltip="{
                        content: 'Credit Return PDF',
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                      @click="fetchPDF(item.id)"
                    >
                      <v-icon class="i-btn-pdf">mdi-file-pdf-box</v-icon>
                    </v-btn>
                    <v-btn
                      :disabled="
                        item.status !== APPROVED || !item.invoiceNumber
                      "
                      icon
                      v-tooltip="{
                        content: 'Fees Invoice PDF',
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                      @click="fetchInvoicePDF(item.id)"
                    >
                      <v-icon class="i-btn-pdf">mdi-receipt-text-check</v-icon>
                    </v-btn>
                  </template>

                  <template v-slot:[`item.status`]="{ item }">
                    <p
                      v-if="item.status === OUTSTANDING"
                      class="g-bold g-border-radius-s i-status-block-orange px-2 py-2 my-1 text-center"
                      v-resize-text="{
                        minFontSize: '13px',
                        maxFontSize: '15px'
                      }"
                    >
                      OUTSTANDING
                    </p>
                    <p
                      v-if="item.status === REJECTED"
                      class="g-bold g-border-radius-s i-status-block-red px-2 py-2 my-1 text-center"
                      v-resize-text="{
                        minFontSize: '13px',
                        maxFontSize: '15px'
                      }"
                    >
                      REJECTED
                    </p>
                    <p
                      v-if="item.status === APPROVED"
                      class="g-bold g-border-radius-s i-status-block-green px-2 py-2 my-1 text-center"
                      v-resize-text="{
                        minFontSize: '13px',
                        maxFontSize: '15px'
                      }"
                    >
                      {{ item.hasRejectedItems ? 'PARTIALLY' : '' }} APPROVED
                    </p>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </template>

          <i-paginator
            :currentPage="currentPage"
            :pageSize="pageSize"
            :pagination="pagination"
            @paginate="handlePagination"
          />
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  fetchCreditReturns,
  fetchCreditReturnPdfUrl,
  fetchCreditReturnInvoicePdfUrl
} from '@/services/creditReturns'
import iPaginator from '@/components/Paginator'
import iProgressLinear from '@/components/ProgressLinear'
import iSearchSort from '@/components/SearchSort'
import { openFile, routeToPaymentTransactions } from '@/utils/common'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import { pickSupplierCostTotal } from '@/utils/supplierAmountDisplay'

const CREDIT_RETURNS_STATUS = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  OUTSTANDING: 'OUTSTANDING'
}

export default {
  components: {
    iEmptyResults,
    iInstructions,
    iPaginator,
    iProgressLinear,
    iSearchSort
  },

  data: () => ({
    loading: false,
    fetchingInvoicePDF: false,
    currentPage: 1,
    pageSize: 10,
    pageRef: 'pageTitle',
    pagination: {
      total: 0,
      totalPages: 0
    },
    query: null,
    creditReturns: [],
    selectedView: CREDIT_RETURNS_STATUS.APPROVED,
    ...CREDIT_RETURNS_STATUS
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('creditReturns', [
      'approvedCreditReturns',
      'rejectedCreditReturns'
    ]),
    ...mapGetters('tenant', [
      'featureCreditReturnsEnabled',
      'isILR',
      'tenantCountryRegion',
      'usesAustralianTaxLabels'
    ]),

    headers() {
      return [
        {
          text: 'CREDIT RETURN\nREFERENCE NUMBER',
          value: 'referenceNumber',
          sortable: false
        },
        { text: 'CREATED DATE', value: 'createdAt', sortable: false },
        { text: 'RESOLVED DATE', value: 'resolvedAt', sortable: false },
        {
          text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`,
          value: 'store.name',
          sortable: false
        },
        {
          text: this.usesAustralianTaxLabels
            ? 'TOTAL CREDIT AMOUNT\n(INC TAX)'
            : 'TOTAL CREDIT AMOUNT',
          value: 'supplierTotalIncFee.amountIncGst',
          align: 'center',
          sortable: false
        },
        {
          text: 'DETAILS',
          value: 'details',
          align: 'center',
          sortable: false,
          width: 120
        },
        {
          text: 'ACTIONS',
          value: 'actions',
          align: 'center',
          sortable: false,
          hideForRejected: true,
          width: 180
        },
        {
          text: 'STATUS',
          value: 'status',
          align: 'center',
          sortable: false,
          width: 140
        }
      ]
    },

    instructionSection() {
      let view = null
      switch (this.selectedView) {
        case CREDIT_RETURNS_STATUS.APPROVED:
          view = 'Approved'
          break
        case CREDIT_RETURNS_STATUS.REJECTED:
          view = 'Rejected'
          break
      }
      return view
    },

    emptyFilteredResultsText() {
      return `No ${this.selectedView} Credit Returns found`
    },

    headersForSelectedView() {
      return this.selectedView === CREDIT_RETURNS_STATUS.REJECTED
        ? this.headers.filter((h) => !h.hideForRejected)
        : this.headers
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    handleHeaderSort(value) {
      this.$refs.searchSort?.sortElements(value)
    },

    isSortedBy(value) {
      return this.$refs.searchSort?.sortBy === value
    },

    isSortAsc(value) {
      return !!this.$refs.searchSort?.sortByOptions.find(
        (option) => option.value === value
      )?.asc
    },

    fetch(supplierId, currentPage, pageSize, status, query) {
      this.loading = true
      currentPage -= 1 // currentPage decreased by 1 as currentPage API starts from 0

      fetchCreditReturns(supplierId, currentPage, pageSize, status, query)
        .then((res) => {
          this.creditReturns = res.creditReturns
          this.pagination = res.pagination
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchInvoicePDF(creditReturnId) {
      this.fetchingInvoicePDF = true
      fetchCreditReturnInvoicePdfUrl(creditReturnId)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Credit Return ${creditReturnId} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetchingInvoicePDF = false
        })
    },

    fetchPDF(creditReturnId) {
      fetchCreditReturnPdfUrl(creditReturnId)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Credit return ${creditReturnId} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
    },

    displayCreditReturnTotal(amount) {
      return pickSupplierCostTotal({
        tenantCountryRegion: this.tenantCountryRegion,
        cost: amount
      })
    },

    handlePagination(page) {
      this.currentPage = page
      this.loading = true
      this.creditReturns = []
      this.fetch(
        this.selectedSupplierId,
        this.currentPage,
        this.pageSize,
        this.selectedView,
        this.query
      )
      if (this.$refs[this.pageRef])
        this.$refs[this.pageRef].scrollIntoView({ behavior: 'smooth' })
    },

    handleViewChange(status) {
      this.selectedView = status
      this.currentPage = 1
      this.loading = true
      this.creditReturns = []
      this.fetch(
        this.selectedSupplierId,
        this.currentPage,
        this.pageSize,
        this.selectedView,
        this.query
      )
    },

    searchQuery(queryObject) {
      if (queryObject.restartPagination) this.currentPage = 1

      this.query = queryObject.query
      this.fetch(
        this.selectedSupplierId,
        this.currentPage,
        this.pageSize,
        this.selectedView,
        this.query
      )
    },

    paymentTransactionsRouteTo(creditReturn) {
      return routeToPaymentTransactions(
        creditReturn,
        creditReturn.referenceNumber
      )
    }
  },

  mounted() {
    // Note: this is a simple guard to ensure that ILRMarketplace users can't browse to this page directly.
    // It is already hidden on the Reports.vue landing page, so if changing the behaviour there you will need to change it here too!
    if (!this.featureCreditReturnsEnabled)
      this.$router.push({ name: 'dashboard' })

    this.fetch(
      this.selectedSupplierId,
      this.currentPage,
      this.pageSize,
      this.selectedView
    )
  },

  watch: {
    approvedCreditReturns() {
      if (this.selectedView === this.APPROVED) {
        this.fetch(this.currentPage, this.pageSize, this.selectedView)
      }
    },

    rejectedCreditReturns() {
      if (this.selectedView === this.REJECTED) {
        this.fetch(this.currentPage, this.pageSize, this.selectedView)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#credit-returns-history {
  .p-btn-pdf {
    color: $link-color;
    &:before {
      font-size: 32px;
    }
  }
}

@media only screen and (min-width: 1264px) {
  #credit-returns-history {
    .p-btn {
      width: 125px;
    }
  }
}
</style>
